// Settings Page - App Store Connect Integration
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useSettingsStore } from '../stores/settingsStore'
import { appStoreService } from '../services/appStoreService'
import { apiKeysService } from '../services/apiKeysService'
import { appsService, type App } from '../services/appsService'
import { TIMEZONES, formatDateTime } from '../utils/dateUtils'
import type { ApiKey } from '../types'
import {
  ANDROID_SDK_COORDINATE,
  APPLE_SDK_REPO_URL,
  APPLE_SDK_REQUIREMENTS,
  APPLE_SDK_VERSION,
  PLATFORM_REQUIREMENTS,
} from '../utils/sdkVersions'

interface SettingsProps {
  selectedAppId?: string
}

export default function Settings({ selectedAppId: selectedAppIdFromNav }: SettingsProps = {}) {
  const { user } = useAuthStore()
  const { timezone, setTimezone } = useSettingsStore()
  
  const [isConnected, setIsConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [showCredentialsForm, setShowCredentialsForm] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [apps, setApps] = useState<App[]>([])
  const [isKeysLoading, setIsKeysLoading] = useState(true)
  const [isAppsLoading, setIsAppsLoading] = useState(true)
  const [isCreatingKey, setIsCreatingKey] = useState(false)
  const [newApiKeyName, setNewApiKeyName] = useState('Default Key')
  const [newApiKeyAppId, setNewApiKeyAppId] = useState('')
  const [createdApiKeyValue, setCreatedApiKeyValue] = useState<string | null>(null)
  const [showAIInstructions, setShowAIInstructions] = useState(false)
  const [selectedAIAppId, setSelectedAIAppId] = useState('')
  const [filterAppId, setFilterAppId] = useState<string | null>(null)

  const [credentials, setCredentials] = useState({
    issuer_id: '',
    key_id: '',
    private_key: '',
  })

  const [integration, setIntegration] = useState<any>(null)

  useEffect(() => {
    loadIntegrationStatus()
    loadApiKeys()
    loadApps()
  }, [])

  const loadIntegrationStatus = async () => {
    try {
      const status = await appStoreService.getCredentials()
      setIsConnected(status.connected)
      setIntegration(status)
    } catch (err) {
      console.error('Failed to load integration status:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const loadApiKeys = async () => {
    try {
      const response = await apiKeysService.listKeys()
      setApiKeys(response.keys || [])
    } catch (err) {
      console.error('Failed to load API keys:', err)
    } finally {
      setIsKeysLoading(false)
    }
  }

  const loadApps = async () => {
    try {
      const appsList = await appsService.getApps()
      setApps(appsList)
      if (!newApiKeyAppId && appsList.length > 0) {
        // If navigated from app card, pre-select that app
        const defaultAppId = selectedAppIdFromNav || appsList[0].id
        setNewApiKeyAppId(defaultAppId)
        // Also set it as the AI instructions default
        setSelectedAIAppId(defaultAppId)
      }
      // Set filter if coming from a specific app
      if (selectedAppIdFromNav) {
        setFilterAppId(selectedAppIdFromNav)
      }
    } catch (err) {
      console.error('Failed to load apps for API key assignment:', err)
    } finally {
      setIsAppsLoading(false)
    }
  }

  const handleSaveCredentials = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    setError(null)
    setSuccess(null)

    try {
      await appStoreService.saveCredentials(credentials)
      setSuccess('App Store Connect integration configured successfully!')
      setShowCredentialsForm(false)
      await loadIntegrationStatus()
      
      // Clear form
      setCredentials({ issuer_id: '', key_id: '', private_key: '' })
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save credentials')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect App Store Connect? This will stop syncing metadata.')) {
      return
    }

    try {
      await appStoreService.deleteCredentials()
      setSuccess('App Store Connect integration disabled')
      setIsConnected(false)
      setIntegration(null)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disconnect')
    }
  }

  const [isSyncing, setIsSyncing] = useState(false)

  const handleTriggerSync = async () => {
    setIsSyncing(true)
    setError(null)
    try {
      await appStoreService.triggerSync()
      setSuccess('Sync started! Checking progress...')

      // Poll for completion — the sync runs in the background on the server
      const poll = setInterval(async () => {
        try {
          const status = await appStoreService.getCredentials()
          if (status.last_synced_at && status.last_synced_at !== integration?.last_synced_at) {
            clearInterval(poll)
            setIntegration(status)
            setIsSyncing(false)
            setSuccess('Sync completed successfully!')
          }
        } catch { /* ignore polling errors */ }
      }, 3000)

      // Stop polling after 2 minutes regardless
      setTimeout(() => {
        clearInterval(poll)
        setIsSyncing(false)
        loadIntegrationStatus()
      }, 120000)
    } catch (err: any) {
      setIsSyncing(false)
      setError(err.response?.data?.error || 'Failed to trigger sync')
    }
  }

  const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsCreatingKey(true)
    setError(null)
    setSuccess(null)
    setCreatedApiKeyValue(null)

    if (!newApiKeyAppId) {
      setError('Select an app before creating an API key.')
      setIsCreatingKey(false)
      return
    }

    try {
      const response = await apiKeysService.createKey(newApiKeyAppId, newApiKeyName.trim() || 'Default Key')
      setCreatedApiKeyValue(response.api_key)
      setSuccess('API key created. Copy and store it now; it is shown only once.')
      await loadApiKeys()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create API key')
    } finally {
      setIsCreatingKey(false)
    }
  }

  const handleRevokeApiKey = async (id: string) => {
    if (!confirm('Revoke this API key? Apps using it will stop sending events.')) {
      return
    }

    setError(null)
    setSuccess(null)

    try {
      await apiKeysService.revokeKey(id)
      setSuccess('API key revoked.')
      await loadApiKeys()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to revoke API key')
    }
  }

  const generateAIInstructions = (appId: string): string => {
    const app = apps.find(a => a.id === appId)
    if (!app) return ''

    // Find the first active API key for this app
    const apiKey = apiKeys.find(k => k.app_id === appId && k.is_active)
    const apiKeyValue = apiKey ? apiKey.key_prefix.replace(/\.\.\./g, 'XXXXXXXXXX') : 'YOUR_API_KEY_HERE'
    const apiKeyNote = apiKey ? `Copy the full key from Settings when you created it` : `No API key found - create one first`
    const appClassName = app.name.replace(/[^a-zA-Z0-9]/g, '')

    // Android apps use the native Kotlin SDK (OneThum/AppStats-Android), which is the
    // functional peer of the Swift SDK and conforms to the same wire protocol.
    if (app.platform === 'android') {
      const appClassNameAndroid = appClassName || 'MyApp'
      return `# Add AppStats Analytics to ${app.name} (Android)

## Overview
AppStats is a real-time analytics platform for iOS and Android apps. The Android SDK is the native Kotlin peer of the Swift SDK and reports to the same backend, so your Android data appears alongside your iOS data in the same dashboard.

## App Information
- **App Name**: ${app.name}
- **Application ID**: ${app.bundle_id}
- **Platform**: Android (Kotlin SDK)
- **API Endpoint**: https://ingest.appstats.app/v1/ingest
- **API Key**: ${apiKeyValue} (${apiKeyNote})

> Use the **same API key family** as your iOS app's account — events are tagged \`platform: android\` and \`sdk_platform: kotlin\` server-side, so iOS and Android show up as peers in AppStats.

## Implementation Steps

### 1. Add the dependency (JitPack)

\`\`\`kotlin
// settings.gradle.kts
dependencyResolutionManagement {
    repositories {
        google()
        mavenCentral()
        maven("https://jitpack.io")
    }
}

// app/build.gradle.kts
dependencies {
    implementation("${ANDROID_SDK_COORDINATE}")
}
\`\`\`

### 2. Configure the SDK

Option A — in code (recommended), from your \`Application\`:

\`\`\`kotlin
import android.app.Application
import com.onethumsoftware.appstats.AppStats

class ${appClassNameAndroid}Application : Application() {
    override fun onCreate() {
        super.onCreate()
        AppStats.configure(this, apiKey = "${apiKeyValue}")
    }
}
\`\`\`

Make sure it's registered in your manifest: \`<application android:name=".${appClassNameAndroid}Application" ...>\`.

Option B — zero-code, via manifest meta-data (auto-init):

\`\`\`xml
<application>
    <meta-data android:name="com.onethumsoftware.appstats.API_KEY" android:value="${apiKeyValue}" />
</application>
\`\`\`

### 3. Track events

\`\`\`kotlin
// Custom event with properties (primitives only)
AppStats.track("purchase_completed", mapOf(
    "product_id" to "premium_subscription",
    "price" to 9.99,
    "currency" to "USD",
))

// Screen views (Compose / single-activity navigation)
AppStats.trackScreen("Settings")

// Identify a signed-in user (sets sticky user_id + signed_in)
AppStats.identify(userId)
\`\`\`

App launch, foreground/background sessions, crashes, and (Activity-based) screen views are tracked automatically.

## Event Types Supported
- \`session_start\` / \`session_end\` — session lifecycle (automatic)
- \`app_launch\` / \`app_foreground\` / \`app_background\` — lifecycle (automatic)
- \`screen_view\` — automatic for Activities; manual via \`trackScreen\`
- \`crash\` — uncaught exceptions, sent on next launch (automatic)
- \`custom\` — any event you define via \`track\`

## Data Collected
The SDK automatically collects: device model, OS version, app version & build number, screen resolution, locale, timezone, session ID, and SDK metadata. Geographic data is derived server-side from IP (country/city granularity only). No advertising IDs, contacts, or precise location.

## Testing
1. Run your app on a device/emulator.
2. Trigger some events.
3. Open the AppStats dashboard at https://appstats.onethum.com and watch the Live Events feed and map — Android events appear next to iOS.

## API Key Security
⚠️ Store your API key securely — inject it via \`BuildConfig\`/Gradle properties or your secrets manager rather than hardcoding it in source committed to git.

## Support
- Dashboard: https://appstats.onethum.com
- Android SDK: https://github.com/OneThum/AppStats-Android

---
*This prompt was generated by AppStats for AI-assisted integration*`
    }

    return `# Add AppStats Analytics to ${app.name}

## Overview
AppStats is a real-time analytics platform for iOS, macOS, tvOS, watchOS and visionOS apps. The Swift SDK tracks sessions, events and crashes, and reports to the same backend as the Kotlin SDK, so all your platforms appear together in one dashboard.

## App Information
- **App Name**: ${app.name}
- **Bundle ID**: ${app.bundle_id}
- **Platform**: ${app.platform} (Swift SDK)
- **Minimum OS**: ${PLATFORM_REQUIREMENTS[app.platform] ?? 'iOS 16.0+'}
- **Toolchain**: ${APPLE_SDK_REQUIREMENTS}
- **API Key**: ${apiKeyValue} (${apiKeyNote})

## Implementation Steps

### 1. Add the package

In Xcode: **File → Add Package Dependencies…**, enter \`${APPLE_SDK_REPO_URL}\`, and choose **Up to Next Major Version** from \`${APPLE_SDK_VERSION}\`.

Or in \`Package.swift\`:

\`\`\`swift
dependencies: [
    .package(url: "${APPLE_SDK_REPO_URL}.git", from: "${APPLE_SDK_VERSION}"),
],
targets: [
    .target(name: "${appClassName || 'MyApp'}", dependencies: [
        .product(name: "AppStats", package: "AppStats-iOS"),
    ]),
]
\`\`\`

### 2. Configure the SDK

Call \`configure\` once at launch. In SwiftUI the recommended call site is a \`.task\` on your root view, so the first frame renders without waiting:

\`\`\`swift
import SwiftUI
import AppStats

@main
struct ${appClassName || 'MyApp'}App: App {
    var body: some Scene {
        WindowGroup {
            ContentView()
                .task {
                    AppStats.configure(apiKey: "${apiKeyValue}")
                }
        }
    }
}
\`\`\`

For UIKit, call it from \`application(_:didFinishLaunchingWithOptions:)\` instead. The heavy initialization is already asynchronous, so it will not block launch.

\`configure\` also takes \`autoTrackScreens\` (default \`true\`) and \`flushInterval\` (default 30 seconds).

### 3. Track events

\`\`\`swift
// Custom event with properties (strings, numbers and booleans only)
AppStats.track("purchase_completed", properties: [
    "product_id": "premium_subscription",
    "price": 9.99,
    "currency": "USD",
])

// Screen views in SwiftUI
ContentView()
    .trackedScreen("Settings")

// A sticky property on this installation
AppStats.setUserProperty("plan", value: "premium")

// Force a send, e.g. before the app terminates
AppStats.flush()
\`\`\`

App launch, foreground/background sessions and crashes are tracked automatically.

## Event Types Supported
- \`session_start\` / \`session_end\` — session lifecycle (automatic)
- \`app_launch\` / \`app_foreground\` / \`app_background\` — lifecycle (automatic)
- \`screen_view\` — automatic with \`autoTrackScreens\`; manual via \`.trackedScreen\`
- \`crash\` — uncaught exceptions, sent on next launch (automatic)
- \`custom\` — any event you define via \`track\`

## Data Collected
The SDK automatically collects: device model, OS version, app version & build number, screen resolution, locale, timezone, session ID, and SDK metadata. Geographic data is derived server-side from IP, rounded to two decimal places (roughly city granularity). No advertising IDs, contacts, or precise location.

The SDK ships a \`PrivacyInfo.xcprivacy\` manifest declaring these. Your own App Store privacy label still needs to cover them — including **Coarse Location**, which the server derives from the request IP.

## Testing
1. Run your app on a device or simulator.
2. Trigger some events.
3. Open the AppStats dashboard at https://appstats.onethum.com and watch the Live Events feed and map.

## API Key Security
⚠️ Store your API key securely — use an Xcode configuration file or your secrets manager rather than hardcoding it in source committed to git. Consider separate keys for debug and release builds.

## Support
- Dashboard: https://appstats.onethum.com
- Swift SDK: ${APPLE_SDK_REPO_URL}

---
*This prompt was generated by AppStats for AI-assisted integration*`
  }

  const handleCopyAIInstructions = () => {
    const instructions = generateAIInstructions(selectedAIAppId)
    navigator.clipboard.writeText(instructions)
    setSuccess('AI instructions copied to clipboard!')
    setTimeout(() => setSuccess(null), 3000)
  }

  if (isLoading) {
    return (
      <div className="p-4 md:p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-800 rounded w-1/2 md:w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-800 rounded w-3/4 md:w-1/2"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-2">Settings</h1>
      <p className="text-gray-400 mb-6 md:mb-8 text-sm md:text-base">Manage your account and integrations</p>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-4 mb-6">
          <p className="text-green-400">{success}</p>
        </div>
      )}

      {/* Account Section */}
      <section className="card mb-6 md:mb-8">
        <h2 className="text-lg md:text-xl font-semibold mb-4">Account</h2>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-gray-400">Email</label>
            <p className="font-medium break-words">{user?.email}</p>
          </div>
          <div>
            <label className="text-sm text-gray-400">Display Name</label>
            <p className="font-medium">{user?.displayName || 'Not set'}</p>
          </div>
        </div>
      </section>

      {/* Display Preferences Section */}
      <section className="card mb-6 md:mb-8">
        <h2 className="text-lg md:text-xl font-semibold mb-2">Display Preferences</h2>
        <p className="text-gray-400 text-sm mb-4">
          Customize how dates and times are displayed throughout the app.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="input w-full"
            >
              {Object.entries(TIMEZONES).map(([region, zones]) => (
                <optgroup key={region} label={region}>
                  {zones.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">
              Current time: {formatDateTime(new Date())}
            </p>
          </div>
        </div>
      </section>

      {/* AI Integration Helper */}
      <section className="card mb-6 md:mb-8">
        <h2 className="text-lg md:text-xl font-semibold mb-2">Instructions for AI Coders</h2>
        <p className="text-gray-400 text-sm mb-4">
          Generate a complete integration prompt for AI coding assistants (Cursor, Claude, GPT Codex, etc.)
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={selectedAIAppId}
            onChange={(e) => setSelectedAIAppId(e.target.value)}
            className="input sm:flex-1"
            disabled={isAppsLoading || apps.length === 0}
          >
            {isAppsLoading && <option value="">Loading apps...</option>}
            {!isAppsLoading && apps.length === 0 && <option value="">Create an app first</option>}
            {!isAppsLoading && !selectedAIAppId && <option value="">Select an app...</option>}
            {!isAppsLoading && apps.map((app) => (
              <option key={app.id} value={app.id}>
                {app.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              if (selectedAIAppId) {
                setShowAIInstructions(true)
              }
            }}
            disabled={!selectedAIAppId}
            className="btn-primary w-full sm:w-auto"
          >
            View Instructions
          </button>
        </div>
      </section>

      {/* SDK API Keys */}
      <section className="card mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg md:text-xl font-semibold mb-2">SDK API Keys</h2>
            <p className="text-gray-400 text-sm">
              Use these keys in your iOS/macOS apps to send analytics events.
            </p>
          </div>
          {filterAppId && (
            <button
              onClick={() => setFilterAppId(null)}
              className="btn-secondary text-sm w-full sm:w-auto"
            >
              Show All Apps
            </button>
          )}
        </div>

        {filterAppId && (
          <div className="bg-blue-500/10 border border-blue-500/50 rounded-lg p-3 mb-4">
            <p className="text-blue-300 text-sm">
              Showing keys for: <strong>{apps.find(a => a.id === filterAppId)?.name}</strong>
            </p>
          </div>
        )}

        {createdApiKeyValue && (
          <div className="bg-amber-500/10 border border-amber-500/50 rounded-lg p-4 mb-4">
            <p className="text-amber-300 font-medium mb-2">New API key (shown once):</p>
            <code className="block text-xs md:text-sm break-all text-amber-100">{createdApiKeyValue}</code>
          </div>
        )}

        <form onSubmit={handleCreateApiKey} className="flex flex-col sm:flex-row gap-3 mb-5">
          <select
            value={newApiKeyAppId}
            onChange={(e) => setNewApiKeyAppId(e.target.value)}
            className="input sm:w-64"
            disabled={isAppsLoading || apps.length === 0}
            required
          >
            {isAppsLoading && <option value="">Loading apps...</option>}
            {!isAppsLoading && apps.length === 0 && <option value="">Create an app first</option>}
            {!isAppsLoading && apps.map((app) => (
              <option key={app.id} value={app.id}>
                {app.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={newApiKeyName}
            onChange={(e) => setNewApiKeyName(e.target.value)}
            placeholder="Key name (e.g. iOS Production)"
            className="input flex-1"
          />
          <button
            type="submit"
            disabled={isCreatingKey || apps.length === 0}
            className="btn-primary w-full sm:w-auto"
          >
            {isCreatingKey ? 'Creating...' : 'Create API Key'}
          </button>
        </form>

        {isKeysLoading ? (
          <p className="text-gray-400 text-sm">Loading API keys...</p>
        ) : (() => {
          const filteredKeys = filterAppId 
            ? apiKeys.filter(k => k.app_id === filterAppId)
            : apiKeys
          
          return filteredKeys.length === 0 ? (
            <p className="text-gray-400 text-sm">
              {filterAppId ? 'No API keys for this app yet.' : 'No API keys yet.'}
            </p>
          ) : (
            <div className="space-y-3">
              {filteredKeys.map((key) => (
              <div
                key={key.id}
                className="p-3 rounded-lg bg-gray-800/50 border border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              >
                <div>
                  <p className="font-medium">{key.name}</p>
                  <p className="text-xs text-gray-300">{key.app_name || key.bundle_id || 'Unassigned app'}</p>
                  <p className="text-xs text-gray-400 font-mono">{key.key_prefix}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Created {formatDateTime(key.created_at).split(',')[0]}
                    {key.last_used_at ? ` · Last used ${formatDateTime(key.last_used_at)}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded ${key.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-300'}`}>
                    {key.is_active ? 'Active' : 'Revoked'}
                  </span>
                  {key.is_active && (
                    <button
                      onClick={() => handleRevokeApiKey(key.id)}
                      className="btn-secondary text-sm"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            ))}
            </div>
          )
        })()}
      </section>

      {/* App Store Connect Integration */}
      <section className="card">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div className="flex-1">
            <h2 className="text-lg md:text-xl font-semibold flex flex-wrap items-center gap-2">
              <span>App Store Connect</span>
              {isConnected && (
                <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded whitespace-nowrap">
                  Connected
                </span>
              )}
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              Sync app metadata, ratings, and reviews from App Store Connect
            </p>
          </div>

          {isConnected && (
            <button
              onClick={handleDisconnect}
              className="btn-secondary text-sm w-full sm:w-auto"
            >
              Disconnect
            </button>
          )}
        </div>

        {isConnected ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 p-3 md:p-4 bg-gray-800/50 rounded-lg">
              <div>
                <label className="text-xs text-gray-400">Issuer ID</label>
                <p className="font-mono text-xs md:text-sm break-all">{integration?.issuer_id}</p>
              </div>
              <div>
                <label className="text-xs text-gray-400">Key ID</label>
                <p className="font-mono text-xs md:text-sm break-all">{integration?.key_id}</p>
              </div>
              <div>
                <label className="text-xs text-gray-400">Connected</label>
                <p className="text-xs md:text-sm">{formatDateTime(integration?.created_at).split(',')[0]}</p>
              </div>
              <div>
                <label className="text-xs text-gray-400">Last Synced</label>
                <p className="text-xs md:text-sm">
                  {integration?.last_synced_at
                    ? formatDateTime(integration.last_synced_at)
                    : 'Never'}
                </p>
              </div>
            </div>

            <button
              onClick={handleTriggerSync}
              disabled={isSyncing}
              className="btn-primary w-full sm:w-auto"
            >
              {isSyncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
        ) : (
          <div>
            {!showCredentialsForm ? (
              <div className="py-4 px-4">
                <p className="text-gray-400 mb-4 text-sm">
                  Connect your App Store Connect account to automatically sync app metadata,
                  ratings, and reviews.
                </p>
                <button
                  onClick={() => setShowCredentialsForm(true)}
                  className="btn-primary"
                >
                  Connect App Store Connect
                </button>
              </div>
            ) : (
              <form onSubmit={handleSaveCredentials} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Issuer ID
                  </label>
                  <input
                    type="text"
                    value={credentials.issuer_id}
                    onChange={(e) => setCredentials({ ...credentials, issuer_id: e.target.value })}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="input w-full"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Find this in App Store Connect → Users and Access → Keys
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Key ID
                  </label>
                  <input
                    type="text"
                    value={credentials.key_id}
                    onChange={(e) => setCredentials({ ...credentials, key_id: e.target.value })}
                    placeholder="XXXXXXXXXX"
                    className="input w-full"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Private Key (.p8 file)
                  </label>
                  <textarea
                    value={credentials.private_key}
                    onChange={(e) => setCredentials({ ...credentials, private_key: e.target.value })}
                    placeholder="-----BEGIN PRIVATE KEY-----
...
-----END PRIVATE KEY-----"
                    className="input w-full font-mono text-sm"
                    rows={10}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Paste the entire contents of your .p8 file
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="btn-primary w-full sm:w-auto"
                  >
                    {isSaving ? 'Saving...' : 'Save Credentials'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCredentialsForm(false)}
                    className="btn-secondary w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>

                <div className="p-4 bg-blue-500/10 border border-blue-500/50 rounded-lg">
                  <h4 className="font-semibold text-blue-400 mb-2">How to get API credentials:</h4>
                  <ol className="text-sm text-gray-300 space-y-1 list-decimal list-inside">
                    <li>Go to App Store Connect → Users and Access</li>
                    <li>Open the Keys tab under "Integrations"</li>
                    <li>Click + to create a new "Team Key" with <strong className="text-blue-300">App Manager</strong> (or Admin) role</li>
                    <li>Download the .p8 file immediately (can't re-download later)</li>
                    <li>Copy the Issuer ID and Key ID</li>
                  </ol>
                </div>
              </form>
            )}
          </div>
        )}
      </section>

      {/* AI Instructions Modal */}
      {showAIInstructions && selectedAIAppId && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-auto">
          <div className="card w-full max-w-4xl max-h-[90vh] overflow-auto custom-scrollbar my-8">
            <div className="sticky top-0 bg-gray-900/95 backdrop-blur-sm pb-4 mb-4 border-b border-white/10">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-semibold mb-1">Instructions for AI Coders</h3>
                  <p className="text-sm text-gray-400">
                    Copy this prompt and paste it into your AI coding assistant
                  </p>
                </div>
                <button
                  onClick={() => setShowAIInstructions(false)}
                  className="btn-secondary text-sm flex-shrink-0"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-800/50 rounded-lg p-4 border border-white/10">
                <pre className="text-xs md:text-sm whitespace-pre-wrap font-mono text-gray-300 leading-relaxed">
                  {generateAIInstructions(selectedAIAppId)}
                </pre>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={handleCopyAIInstructions}
                  className="btn-primary flex-1"
                >
                  Copy to Clipboard
                </button>
                <button
                  onClick={() => setShowAIInstructions(false)}
                  className="btn-secondary flex-1"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
